import styled from '@emotion/styled';
import type { FallbackProps } from 'react-error-boundary';
import { useTranslation } from '@hotjar/translations';
import { tokens, Icon, Text } from '@hotjar/hotsauce';

interface ErrorFallbackComponentProps extends FallbackProps {
  type?: 'page' | 'component';
  alignment?: 'top' | 'center' | 'bottom';
}

const getAlignmentValue = (alignment: ErrorFallbackComponentProps['alignment']): string => {
  switch (alignment) {
    case 'top':
      return 'flex-start';
    case 'bottom':
      return 'flex-end';
    default:
      return 'center';
  }
};

const Wrapper = styled.div<{
  type: ErrorFallbackComponentProps['type'];
  alignment: ErrorFallbackComponentProps['alignment'];
}>`
  align-items: center;
  display: flex;
  flex-direction: column;
  height: ${({ type }) => (type === 'page' ? '100vh' : '100%')};
  justify-content: ${({ alignment }) => `${getAlignmentValue(alignment)}`};
  margin: 1vh auto;
  text-align: center;
`;

const Caption = styled(Text)`
  margin: ${tokens.spacer.x1} 0 0;
  color: ${tokens.color.text_light};
`;

export const ErrorFallbackComponent = ({
  alignment,
  type = 'page',
}: ErrorFallbackComponentProps): JSX.Element => {
  const { t } = useTranslation('common');

  return (
    <Wrapper type={type} alignment={alignment} data-testid="error-page">
      <Icon icon="error" size="l" />
      <Caption as="h5" textType="header5">
        {t('error.generic')}
      </Caption>
    </Wrapper>
  );
};
